import { convertCelsiusToFahrenheit } from "@tod-ui/helpers/utils";
import { stringSortCollator, titleCase } from "@tod-ui/helpers/strings";
import { daysAgo } from "@tod-ui/helpers/datetime";

export const AWAITING_DEVICE_DAYS_URGENT = 15;
export const AWAITING_DEVICE_DAYS_WARN = 6;

export const END_SERVICE_MIN_DAYS_SINCE_MOVE_IN = 30;

export function isEndServiceEligibleByMoveIn(moveInAt) {
  if (!moveInAt) return false;
  return daysAgo(moveInAt) >= END_SERVICE_MIN_DAYS_SINCE_MOVE_IN;
}

export const ENROLLMENT_STATUS = {
  AWAITING_DEVICE: "awaiting_device",
  ACTIVE: "active",
  ENDED: "ended",
};

export function isAwaitingDevice(unit) {
  return unit?.enrollmentInfo?.status === ENROLLMENT_STATUS.AWAITING_DEVICE;
}

export function isScheduled(unit) {
  return !!unit?.scheduled;
}

export function countScheduledUnits(units) {
  return (units || []).filter(isScheduled).length;
}

export function scheduledUnitsSubLabel(units) {
  const count = countScheduledUnits(units);
  return count > 0 ? `${count} scheduled` : null;
}

export function daysWithoutDeviceLabel(days) {
  if (days === null || days === undefined) return "—";
  if (days < 0) return "Scheduled";
  return `${days} day${days !== 1 ? "s" : ""} without device`;
}
export const SUBSTANTIAL_COLOR = "#ff9800"; // orange 500
export const MODERATE_COLOR = "#d84315"; // deep-orange 800
export const CRITICAL_COLOR = "#7b1818"; // maroon
export const SCHEDULED_COLOR = "#6a1b9a"; // deep-purple 800

export function awaitingDaysChipColor(days) {
  if (days === null || days === undefined || days < 0) return "#f5f5f5";
  if (days >= AWAITING_DEVICE_DAYS_URGENT) return "#f5e1e1"; // critical tint
  if (days >= AWAITING_DEVICE_DAYS_WARN) return "#fff3e0"; // moderate tint
  return "#fff8e1"; // substantial tint
}

export function awaitingDaysTextColor(days) {
  if (days === null || days === undefined || days < 0) return "#9e9e9e";
  if (days >= AWAITING_DEVICE_DAYS_URGENT) return CRITICAL_COLOR;
  if (days >= AWAITING_DEVICE_DAYS_WARN) return MODERATE_COLOR;
  return SUBSTANTIAL_COLOR;
}

// Solid chip variants used where the chip sits on a tinted row background
// and needs stronger contrast than the outlined chip provides.
export function awaitingDaysSolidChipColor(days) {
  if (days === null || days === undefined) return "#e0e0e0";
  if (days < 0) return SCHEDULED_COLOR;
  if (days >= AWAITING_DEVICE_DAYS_URGENT) return CRITICAL_COLOR;
  if (days >= AWAITING_DEVICE_DAYS_WARN) return MODERATE_COLOR;
  return SUBSTANTIAL_COLOR;
}

export function awaitingDaysSolidTextColor(days) {
  if (days === null || days === undefined) return "#757575";
  return "#ffffff";
}

export function enrollmentStatusLabel(status) {
  if (status === ENROLLMENT_STATUS.AWAITING_DEVICE) return "Awaiting Device";
  if (status === ENROLLMENT_STATUS.ACTIVE) return "Active";
  if (status === ENROLLMENT_STATUS.ENDED) return "Ended";
  return null;
}

export function enrollmentStatusChipColor(status) {
  if (status === ENROLLMENT_STATUS.AWAITING_DEVICE) return "#fff3e0";
  if (status === ENROLLMENT_STATUS.ACTIVE) return "#e8f5e9";
  if (status === ENROLLMENT_STATUS.ENDED) return "#ffebee";
  return "#f5f5f5";
}

export function enrollmentStatusTextColor(status) {
  if (status === ENROLLMENT_STATUS.AWAITING_DEVICE) return "#e65100";
  if (status === ENROLLMENT_STATUS.ACTIVE) return "#2e7d32";
  if (status === ENROLLMENT_STATUS.ENDED) return "#c62828";
  return "#9e9e9e";
}

export function smartUnitTemperatureColor(temperature) {
  const temperatureInFahrenheit = convertCelsiusToFahrenheit(temperature);
  if (temperatureInFahrenheit < 55) {
    return "blue";
  } else if (temperatureInFahrenheit <= 80) {
    return "success";
  } else {
    return "error";
  }
}

export function smartUnitHumidityColor(humidity) {
  return humidity <= 69 ? "success" : "error";
}

export function getOfficeInventoryHeaders() {
  return [
    {
      text: "Actions",
      value: "action",
      width: "1%",
      class: "action-column",
      cellClass: "action-column",
      filterable: false,
      sortable: false,
    },
    {
      text: "Smart Unit #",
      value: "name",
      width: "15%",
    },
    // { text: "Unit Type", value: "unitTypeName", width: "10%" },
    // { text: "Unit Size", value: "unitTypeSize", width: "10%" },
    {
      text: "Device #",
      value: "devicesName",
      width: "10%",
    },
    {
      text: "Device Health",
      value: "devicesHealth",
      width: "10%",
      sortable: false,
      class: "non-export",
      cellClass: "non-export",
    },
    {
      text: "Last Motion",
      value: "devicesLastMotion",
      width: "15%",
      class: "text-no-wrap",
    },
    {
      text: "Last Assigned Unit",
      value: "devicesLastAssignedTo",
      width: "15%",
      class: "text-no-wrap",
      sortable: false,
    },
    {
      text: "Manage Device",
      value: "manage",
      width: "1%",
      class: "action-column",
      cellClass: "action-column",
      align: "center",
      sortable: false,
    },
  ];
}

export function getPreinstalledInventoryHeaders() {
  return [
    {
      text: "Actions",
      value: "action",
      width: "1%",
      class: "action-column",
      cellClass: "action-column",
      filterable: false,
      sortable: false,
    },
    {
      text: "Smart Unit #",
      value: "name",
      width: "10%",
    },
    { text: "Unit Type", value: "unitTypeName", width: "10%" },
    { text: "Unit Size", value: "unitTypeSize", width: "10%" },
    {
      text: "Device #",
      value: "devicesName",
      width: "15%",
    },
    {
      text: "Device Health",
      value: "devicesHealth",
      width: "10%",
      sortable: false,
      class: "non-export",
      cellClass: "non-export",
    },
    {
      text: "Last Motion",
      value: "devicesLastMotion",
      width: "20%",
      class: "text-no-wrap",
    },
    {
      text: "Manage Device",
      value: "manage",
      width: "1%",
      class: "action-column",
      cellClass: "action-column",
      align: "center",
      sortable: false,
    },
  ];
}

export function getUnavailableInventoryHeaders() {
  return [
    // {
    //   text: "Smart Unit #",
    //   value: "name",
    //   width: "10%",
    // },
    {
      text: "Device #",
      value: "devicesName",
      width: "20%",
    },
    {
      text: "Device Status",
      value: "devicesHealth",
      width: "25%",
      toString: (device) =>
        device.endedService
          ? "Ended Service"
          : device.disposition != "in_service"
          ? titleCase(device.disposition)
          : "Lost Contact",
    },
    {
      text: "Last Motion",
      value: "devicesLastMotion",
      width: "20%",
      class: "text-no-wrap",
    },
    {
      text: "Last Assigned Unit",
      value: "devicesLastAssignedTo",
      width: "20%",
      class: "text-no-wrap",
      sortable: false,
    },
    {
      text: "Manage Device",
      value: "manage",
      width: "1%",
      class: "action-column",
      cellClass: "action-column",
      align: "center",
      sortable: false,
    },
  ];
}

export function commonCustomFilter(value, search, item) {
  if (!search) return true;
  const _search = search.toLowerCase();

  if (item.name && item.name.toLowerCase().indexOf(_search) >= 0) return true;

  if (
    item.devicesName.find((d) => d.toLowerCase().indexOf(_search) >= 0) !==
    undefined
  )
    return true;

  if (
    item.devicesLastAssignedTo &&
    item.devicesLastAssignedTo.find(
      (d) => d.toLowerCase().indexOf(_search) >= 0
    ) !== undefined
  )
    return true;

  return false;
}

export function commonCustomSort(items, sortBy, sortDesc) {
  const _sortBy = sortBy[0];
  const _sortDesc = sortDesc[0] ? -1 : 1;

  items.sort((a, b) => {
    if (_sortBy === "name") {
      if (a.type === "device" && b.type !== "device") return 1 * _sortDesc;
      if (b.type === "device" && a.type !== "device") return -1 * _sortDesc;
      return stringSortCollator.compare(a.name || "", b.name || "") * _sortDesc;
    } else if (_sortBy === "devicesName") {
      const aDevices = a.devicesName
        .sort((da, db) => stringSortCollator.compare(da, db) * _sortDesc)
        .join(" ");
      const bDevices = b.devicesName
        .sort((da, db) => stringSortCollator.compare(da, db) * _sortDesc)
        .join(" ");

      return stringSortCollator.compare(aDevices, bDevices) * _sortDesc;
    } else if (_sortBy === "devicesLastMotion") {
      return sortByLastMotion(a, b, _sortDesc);
    } else if (_sortBy === "devicesHealth") {
      return sortByDevicesHealth(a, b, _sortDesc);
    } else {
      if (typeof a[_sortBy] === "string") {
        if (b[_sortBy] === " - ") return -1;
        return stringSortCollator.compare(a[_sortBy], b[_sortBy]) * _sortDesc;
      }
      return (b[_sortBy] < a[_sortBy] ? -1 : 1) * _sortDesc;
    }
  });

  return items;
}

function sortByLastMotion(a, b, sortDirection) {
  if (a.devices.length === 1 && b.devices.length === 1) {
    var AextContactDate = a.devices[0].motionStartedAt;
    var BextContactDate = b.devices[0].motionStartedAt;

    AextContactDate =
      AextContactDate == null ? "1970-01-01T00:00:00.000000Z" : AextContactDate;
    BextContactDate =
      BextContactDate == null ? "1970-01-01T00:00:00.000000Z" : BextContactDate;

    return (BextContactDate < AextContactDate ? -1 : 1) * sortDirection;
  }

  var AextContactDate = a.devices[0].motionStartedAt;
  var BextContactDate = b.devices[0].motionStartedAt;

  if (a.devices.length > 1) {
    for (const dev in a.devices) {
      const currentDate = a.devices[dev].motionStartedAt;
      if (currentDate == null) continue;

      if (sortDirection < 0 && currentDate > AextContactDate) {
        AextContactDate = currentDate;
      } else if (
        sortDirection > 0 &&
        (AextContactDate == null || currentDate < AextContactDate)
      ) {
        AextContactDate = currentDate;
      }
    }
  }

  if (b.devices.length > 1) {
    for (const dev in b.devices) {
      const currentDate = b.devices[dev].motionStartedAt;
      if (currentDate == null) continue;

      if (sortDirection < 0 && currentDate > BextContactDate) {
        BextContactDate = currentDate;
      } else if (
        sortDirection > 0 &&
        (BextContactDate == null || currentDate < BextContactDate)
      ) {
        BextContactDate = currentDate;
      }
    }
  }

  AextContactDate =
    AextContactDate == null ? "1970-01-01T00:00:00.000000Z" : AextContactDate;
  BextContactDate =
    BextContactDate == null ? "1970-01-01T00:00:00.000000Z" : BextContactDate;

  return (BextContactDate < AextContactDate ? -1 : 1) * sortDirection;
}

function sortByDevicesHealth(a, b, sortDirection) {
  const aDispositions = a.devices.map((dev) => dev.disposition).sort();
  const aDisposition =
    sortDirection < 0 ? aDispositions.pop() : aDispositions[0];

  const bDispositions = b.devices.map((dev) => dev.disposition).sort();
  const bDisposition =
    sortDirection < 0 ? bDispositions.pop() : bDispositions[0];

  return stringSortCollator.compare(aDisposition, bDisposition) * sortDirection;
}

export function createPrintMethods(tabIndex, updateItemsPerPageFn) {
  return {
    async triggerShowAllForPrint() {
      this.originalItemsPerPage = this.tableState.itemsPerPage;
      this.tableState.itemsPerPage = -1;
      await this.$nextTick();
    },

    async waitForDataToLoad() {
      while (this.loading) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      await this.$nextTick();
      await new Promise((resolve) => setTimeout(resolve, 500));
    },

    async handleAfterPrint() {
      if (this.originalItemsPerPage) {
        this.tableState.itemsPerPage = this.originalItemsPerPage;
        this.originalItemsPerPage = null;
        await this.$nextTick();
      }
    },
  };
}

// Common export methods
export function createExportMethods(tabIndex) {
  return {
    async triggerShowAllForExport() {
      this.originalItemsPerPage = this.tableState.itemsPerPage;
      this.originalDataLength = this.inventory.length;
      this.isWaitingForExport = true;
      this.tableState.itemsPerPage = -1;
      await this.$nextTick();
    },

    async waitForExportData() {
      while (this.loading) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const expectedCount = this.paginationTracking.totalCount || 0;
      const currentLength = this.inventory.length;

      if (currentLength >= expectedCount && expectedCount > 0) {
        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 300));
        return;
      }

      if (this.isWaitingForExport) {
        await new Promise((resolve) => {
          this.exportDataResolver = resolve;

          setTimeout(() => {
            if (
              this.isWaitingForExport &&
              this.exportDataResolver === resolve
            ) {
              resolve();
              this.isWaitingForExport = false;
              this.exportDataResolver = null;
            }
          }, 100);
        });

        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    },

    async handleAfterExport() {
      this.isWaitingForExport = false;
      this.exportDataResolver = null;

      if (this.originalItemsPerPage) {
        this.tableState.itemsPerPage = this.originalItemsPerPage;
        this.originalItemsPerPage = null;
        this.originalDataLength = null;
        await this.$nextTick();
      }
    },

    getCurrentInventoryData() {
      return this.inventory;
    },
  };
}

// Common inventory watcher
export function createInventoryWatcher() {
  return {
    inventory: {
      handler(newData, oldData) {
        const newLength = newData ? newData.length : 0;

        if (this.isWaitingForExport && this.exportDataResolver) {
          const expectedCount = this.paginationTracking.totalCount || 0;

          if (
            newLength >= expectedCount ||
            newLength >= (this.originalDataLength || 0) + 5
          ) {
            this.exportDataResolver();
            this.isWaitingForExport = false;
            this.exportDataResolver = null;
          }
        }
      },
      immediate: false,
    },
  };
}

export function getCommonInventoryData() {
  return {
    originalItemsPerPage: null,
    tableKey: 0,
  };
}

export function createInventoryExportData() {
  return {
    isWaitingForExport: false,
    exportDataResolver: null,
  };
}

export const COMMON_FOOTER_PROPS = {
  "items-per-page-options": [10, 25, 50, -1],
};
